import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import NewsImage from '../components/NewsImage';
import Pagination from '../components/Pagination';
import {
  getCachedAuthorNews,
  loadAuthorNews,
} from '../services/profileNewsService';
import '../styles/AdminArticlesList.css';

const logProfileTiming = (message, startedAt) => {
  if (process.env.NODE_ENV === 'development') {
    const elapsed = startedAt ? ` ${(performance.now() - startedAt).toFixed(1)}ms` : '';
    console.debug(`[${message}]${elapsed}`);
  }
};

const AuthorNewsSkeleton = ({ count = 4 }) => (
  <ul className="admin-articles-list" aria-hidden="true">
    {Array.from({ length: count }).map((_, index) => (
      <li key={index} className="article-skeleton-item">
        <div className="article-skeleton-thumb" />
        <div className="article-skeleton-lines">
          <div className="article-skeleton-line article-skeleton-line--title" />
          <div className="article-skeleton-line article-skeleton-line--date" />
        </div>
      </li>
    ))}
  </ul>
);

const AdminArticlesList = ({
  uid,
  username,
  onCountChange,
}) => {
  const cachedNews = getCachedAuthorNews(uid, username);
  const [articles, setArticles] = useState(
    Array.isArray(cachedNews) ? cachedNews : []
  );
  const [newsInitialLoading, setNewsInitialLoading] = useState(!cachedNews);
  const [newsRefreshing, setNewsRefreshing] = useState(false);
  const [newsError, setNewsError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const articlesPerPage = 40;

  useEffect(() => {
    let active = true;
    const cached = getCachedAuthorNews(uid, username);
    const requestStartedAt = performance.now();

    setArticles(Array.isArray(cached) ? cached : []);
    setNewsInitialLoading(!cached);
    setNewsRefreshing(Boolean(cached));
    setNewsError('');
    setCurrentPage(1);
    logProfileTiming('PROFILE NEWS REQUEST START');

    loadAuthorNews(uid, username)
      .then((loadedArticles) => {
        if (!active) return;
        setArticles(loadedArticles);
        onCountChange?.(loadedArticles.length);
        logProfileTiming('PROFILE NEWS REQUEST COMPLETE', requestStartedAt);
        logProfileTiming('PROFILE COUNT COMPLETE', requestStartedAt);
      })
      .catch((requestError) => {
        if (!active) return;
        if (process.env.NODE_ENV === 'development') {
          console.error('Помилка при отриманні статей:', requestError);
        }
        setNewsError('Не вдалося завантажити написані новини');
      })
      .finally(() => {
        if (!active) return;
        setNewsInitialLoading(false);
        setNewsRefreshing(false);
      });

    if (cached) {
      onCountChange?.(cached.length);
    }

    return () => {
      active = false;
    };
  }, [onCountChange, uid, username]);

  const totalPages = Math.ceil(articles.length / articlesPerPage);
  const indexOfLastArticle = currentPage * articlesPerPage;
  const indexOfFirstArticle = indexOfLastArticle - articlesPerPage;
  const currentArticles = articles.slice(indexOfFirstArticle, indexOfLastArticle);

  return (
    <div className="admin-articles" aria-busy={newsRefreshing}>
      <h2>Написані новини</h2>

      {newsInitialLoading && articles.length === 0 ? (
        <AuthorNewsSkeleton />
      ) : newsError && articles.length === 0 ? (
        <p className="admin-articles-error">{newsError}</p>
      ) : articles.length === 0 ? (
        <p className="admin-articles-empty">Написаних новин поки немає</p>
      ) : (
        <>
          <ul className="admin-articles-list">
            {currentArticles.map((article) => (
              <li key={article.id}>
                <NewsImage
                  news={article}
                  alt={article.title}
                  className="article-thumb"
                  loading="lazy"
                />

                <div className="article-info">
                  <Link to={`/news/${article.id}`} className="article-link">
                    {article.title}
                  </Link>
                  <span className="article-date">
                    {article.day}.{article.month}.{article.year}
                  </span>
                </div>
              </li>
            ))}
          </ul>

          {totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              disabled={newsRefreshing}
              pageType="profile_articles"
            />
          )}
        </>
      )}
    </div>
  );
};

export { AuthorNewsSkeleton };
export default AdminArticlesList;
